import { useDispatch, useSelector } from 'react-redux';
import OrderAPIStepperFooter from '../StepperFooter/OrderAPIStepperFooter';
import {
  updateSelectedFieldsMapping,
  updateWebhookURLMapping,
} from '../../../../../redux/connectedComponent/orderAPI/orderAPI.slice';
import {
  Card,
  IconCheck,
  Input,
  SearchInput,
  INPUT_SIZES,
  Checkbox,
  IconArrowDropDown,
  IconArrowDropUp,
  IconInfo,
  withTooltip,
  TOOLTIP_HORIZONTAL_ALIGNMENTS,
  TOOLTIP_VERTICAL_ALIGNMENTS,
  THEMES,
  ListItem,
} from 'cdk-radial';
import styled from 'styled-components';
import { useState, useEffect } from 'react';
import './SelectAPIFields.scss';
import { ORDER_APIS_STEPPER_FOOTER } from '../../../../../constants/APIDirectoryConstants';
import { API_TYPES } from '../../../../../constants/APIDirectoryConstants';
import { Constants } from '../../../../../constants/constants';
const _ = require('lodash');

const StyledIconCheck = styled(IconCheck)`
  fill: white;
  background-color: #196fe1;
  border-radius: 20px;
`;
const StyledIconInfo = styled(IconInfo)`
  fill: white;
  border-radius: 20px;
`;

export const getAllInstanceFieldMerged = (apiInstances) => {
  const uniqueFieldObj = {};
  let numberOfApiInstances = apiInstances?.length;
  for (const instance of apiInstances) {
    let version = instance?.version ?? 'nullVersion';
    for (const fieldObj of instance.fields) {
      // add key values to the uniqueFieldObj for 1st instance
      let fieldName = fieldObj.name;
      let currentField = uniqueFieldObj[fieldName];
      if (!currentField) {
        uniqueFieldObj[fieldName] = {
          versions: [version],
          numberOfApiInstances,
          ...fieldObj,
        };
      } else {
        let versions;
        // if description is missing check for next instance and update
        if (!currentField.versions.includes(version)) {
          versions = [...currentField.versions, version];
        } else {
          versions = [version];
        }
        if (!currentField.description && fieldObj.description) {
          currentField = { ...fieldObj };
        }
        if (fieldObj.description && fieldObj.example) {
          currentField = { ...fieldObj };
        }
        uniqueFieldObj[fieldName] = {
          ...currentField,
          versions,
          numberOfApiInstances,
        };
      }
    }
  }
  for (let key in uniqueFieldObj) {
    if (
      uniqueFieldObj[key]?.numberOfApiInstances > 1 &&
      uniqueFieldObj[key]?.numberOfApiInstances !==
        uniqueFieldObj[key]?.versions?.length &&
      uniqueFieldObj[key]?.versions[0] != 'nullVersion'
    ) {
      uniqueFieldObj[key].versionsText = `Only in ${uniqueFieldObj[key].versions
        .map((v) => `v${v} `)
        .toString()}`;
    }
  }
  let sortedUniqueFieldsObj = Object.entries(uniqueFieldObj).sort(
    ([key1], [key2]) => key1.localeCompare(key2)
  );
  sortedUniqueFieldsObj = Object.fromEntries(sortedUniqueFieldsObj);
  return Object.values(sortedUniqueFieldsObj);
};

const DivWithTooltip = ({ index, text }) => {
  const tooltipProps = {
    id: `test-tooltip-id-${index}`,
    'data-testid': `test-tooltip-id-${index}`,
    text: (
      <span className="tooltip-text-wrapper">
        <StyledIconInfo />
        <span className="tooltip-text">No configuration needed</span>
      </span>
    ),
    theme: THEMES.LIGHT,
  };

  const WrappedDivListItem = (props) => {
    return (
      <div className="wrapped-div-list-item" {...props}>
        {text}
      </div>
    );
  };

  const MenuItemWithTooltip = withTooltip(WrappedDivListItem, tooltipProps);

  return (
    <MenuItemWithTooltip
      style={{ zIndex: 999999, backgoundColor: 'red' }}
      horizontalAlignment={TOOLTIP_HORIZONTAL_ALIGNMENTS.MIDDLE}
      marginAroundElement={2}
      verticalAlignment={TOOLTIP_VERTICAL_ALIGNMENTS.BOTTOM}
    />
  );
};

const SelectAPIFields = ({ setActiveStep, activeStep }) => {
  const dispatch = useDispatch();
  const state = useSelector((state) => state);
  const cart = state?.orderAPISlice?.cart;
  const [searchInputValue, setSearchInputValue] = useState('');
  const [sortByAscending, setSortByAscending] = useState(true);
  const apiFieldMapping = state?.orderAPISlice?.selectedFieldsMapping;
  const [selectedFieldMapping, setSelectedFieldMapping] = useState(
    new Map(Object.entries(apiFieldMapping))
  );
  const webhookURLMappings = state?.orderAPISlice?.webhookURLMapping;

  const [results, setResults] = useState([]);
  const [selectedAPI, setSelectedAPI] = useState({});
  const [selectedAPIFields, setSelectedAPIFields] = useState([]);
  const [selectedAPIWebhook, setSelectedAPIWebhook] = useState('');
  const asyncApis = cart?.filter((api) => api.apiType === API_TYPES.ASYNC);
  const [isValidUrl, setIsValidUrl] = useState(true);
  const [isContinueDisabled, setIsContinueDisabled] = useState(true);
  const [debouncedValue, setDebouncedValue] = useState('');
  const verticalScopingEnabledApis = cart?.filter(
    (api) => api?.details?.verticalScoping
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(selectedAPIWebhook);
    }, 300); // Adjust the delay as needed

    // Cleanup the timeout if inputValue changes or component unmounts
    return () => {
      clearTimeout(handler);
    };
  }, [selectedAPIWebhook]);

  useEffect(() => {
    if (validateUrl(selectedAPIWebhook) || selectedAPIWebhook === '') {
      if (selectedAPI?.apiId)
        if (selectedAPIWebhook !== '') {
          dispatch(
            updateWebhookURLMapping({
              ...webhookURLMappings,
              [selectedAPI.apiId]: selectedAPIWebhook,
            })
          );
        } else {
          let updatedWebhookURLMapping = _.cloneDeep(webhookURLMappings);
          delete updatedWebhookURLMapping[selectedAPI?.apiId];
          dispatch(
            updateWebhookURLMapping({
              ...updatedWebhookURLMapping,
            })
          );
        }
    }
  }, [debouncedValue]);

  useEffect(() => {
    const configuredApis = Object.keys(apiFieldMapping);
    if (
      configuredApis.length !== verticalScopingEnabledApis.length ||
      asyncApis?.length !== Object.keys(webhookURLMappings)?.length
    ) {
      setIsContinueDisabled(true);
    } else {
      setIsContinueDisabled(false);
    }
  }, [apiFieldMapping, webhookURLMappings, cart]);

  useEffect(() => {
    const defaultAPI = cart?.find(
      (api) =>
        (api?.details?.verticalScoping !== undefined &&
          api?.details?.verticalScoping !== false) ||
        api.apiType === API_TYPES.ASYNC
    );
    const fields = getAllInstanceFieldMerged(
      Object.values(defaultAPI?.details?.fields?.instances || {})
    );
    setResults(fields);
    setSelectedAPIFields(fields);
    setSelectedAPI(defaultAPI);
    setSelectedAPIWebhook(webhookURLMappings[defaultAPI?.apiId]);
  }, []);

  const validateUrl = (
    value,
    { https = false, localhost = false, canBeEmpty = false } = {}
  ) => {
    let re;
    if (https) {
      if (localhost) {
        re =
          /^(https:\/\/)?(www)?[a-z0-9]+([-.]{1}[a-z0-9]+)*((\.[a-z]{2,63})|(:[0-9]{1,5}))(\/.*)?$/;
      } else {
        re =
          /^https:\/\/(www)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,63}(:[0-9]{1,5})?(\/.*)?$/;
      }
    } else {
      if (localhost) {
        re =
          /^((http|https):\/\/)?(www)?[a-z0-9]+([-.]{1}[a-z0-9]+)*((\.[a-z]{2,63})|(:[0-9]{1,5}))(\/.*)?$/;
      } else {
        re =
          /^(http|https):\/\/(www)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,63}(:[0-9]{1,5})?(\/.*)?$/;
      }
    }
    return validateField(value, re, canBeEmpty);
  };
  const validateField = (str, re = /./, canBeEmpty = false) => {
    if (validateNotEmpty(str)) {
      return re.test(str.trim());
    }
    return canBeEmpty;
  };
  const validateNotEmpty = (str) => {
    const val = str && str.trim();
    if (val && val.length) {
      return true;
    }
    return false;
  };
  const validateWebhookUrl = (webhookURL) => {
    let newWebhookURL = webhookURL;
    if (
      webhookURL.length === 0 ||
      (!!webhookURL && webhookURL.startsWith('http://'))
    ) {
      setIsValidUrl(false);
    } else {
      if (!!webhookURL && !webhookURL.startsWith('https://')) {
        newWebhookURL = 'https://' + newWebhookURL;
        setSelectedAPIWebhook(webhookURL);
      }
      if (validateUrl(newWebhookURL, { canBeEmpty: true })) {
        setIsValidUrl(true);
      } else {
        setIsValidUrl(false);
      }
    }
  };

  const handleSelectAllCheckBox = (event) => {
    const selectedApiId = selectedAPI.apiType === API_TYPES.REST ?`${API_TYPES.PROVIDER_ID_PREFIX}-${selectedAPI.apiId}`: selectedAPI.apiId;
    const isChecked = event.target.checked;
    if (isChecked) {
      let fields = [];
      selectedAPIFields.map((a) => fields.push(a.name));
      let map = new Map(selectedFieldMapping);
      map.set(selectedApiId, [...fields]);
      setSelectedFieldMapping(map);
      const plainObject = Object.fromEntries(map.entries());
      dispatch(updateSelectedFieldsMapping(plainObject));
    } else {
      let map = new Map(selectedFieldMapping);
      map.delete(selectedApiId);
      setSelectedFieldMapping(map);
      const plainObject = Object.fromEntries(map.entries());
      dispatch(updateSelectedFieldsMapping(plainObject));
    }
  };

  const handleSearchInputChange = (searchInput) => {
    setSearchInputValue(searchInput);
    setResults(
      selectedAPIFields.filter((a) =>
        a.name.toLowerCase().includes(searchInput.toLowerCase())
      )
    );
  };

  const handleSortByAscending = (isAscending) => {
    const sortOrder = isAscending ? 1 : -1;
    setResults(
      results.sort(
        (a, b) =>
          sortOrder * a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      )
    );
    setSortByAscending(isAscending);
  };
  const handleAPISelection = (element) => {
    setSelectedAPI(element);
    const fields = getAllInstanceFieldMerged(
      Object.values(element?.details?.fields?.instances || {})
    );
    setResults(fields);
    setSelectedAPIFields(fields);
    setSearchInputValue('');
    setSelectedAPIWebhook(webhookURLMappings[element?.apiId]);
    setIsValidUrl(true);
  };

  const handleSelectCheckBox = (event, element, selectedAPI) => {
    const selectedApiId = selectedAPI.apiType === API_TYPES.REST ?`${API_TYPES.PROVIDER_ID_PREFIX}-${selectedAPI.apiId}`: selectedAPI.apiId;
    if (event.target.checked === true) {
      if (!selectedFieldMapping.get(selectedApiId)) {
        let map = new Map(selectedFieldMapping);
        map.set(selectedApiId, [element.name]);
        setSelectedFieldMapping(map);
        const plainObject = Object.fromEntries(map.entries());
        dispatch(updateSelectedFieldsMapping(plainObject));
      } else {
        let map = new Map(selectedFieldMapping);
        map.set(selectedApiId, [
          ...map.get(selectedApiId),
          element.name,
        ]);
        setSelectedFieldMapping(map);
        const plainObject = Object.fromEntries(map.entries());
        dispatch(updateSelectedFieldsMapping(plainObject));
      }
    } else {
      if (selectedFieldMapping.get(selectedApiId)) {
        let map = new Map(selectedFieldMapping);
        let apiFields = selectedFieldMapping.get(selectedApiId);
        let updatedFields = apiFields.filter((a) => a !== element.name);
        if (updatedFields.length === 0) {
          map.delete(selectedApiId);
          setSelectedFieldMapping(map);
          const plainObject = Object.fromEntries(map.entries());
          dispatch(updateSelectedFieldsMapping(plainObject));
        } else {
          map.set(selectedApiId, [...updatedFields]);
          setSelectedFieldMapping(map);
          const plainObject = Object.fromEntries(map.entries());
          dispatch(updateSelectedFieldsMapping(plainObject));
        }
      }
    }
  };
  const handleWebhookInput = (webhookURL) => {
    setSelectedAPIWebhook(webhookURL);
    validateWebhookUrl(webhookURL);
  };
  const checkIsAPIConfigured = (api) => {
    if (api?.apiType === API_TYPES.REST) {
      if (
        api?.details?.verticalScoping &&
        selectedFieldMapping.get(`${API_TYPES.PROVIDER_ID_PREFIX}-${api.apiId}`)
      )
        return true;
    } else if (
      (!api?.details?.verticalScoping ||
        selectedFieldMapping.get(api.apiId)) &&
      webhookURLMappings[api?.apiId]
    )
      return true;
    return false;
  };

  const sortApis = (apis) => {
    const apisWithConfig = [];
    const apisWithOutConfig = [];

    apis.forEach((api) => {
      if (api?.apiType === API_TYPES.ASYNC || api?.details?.verticalScoping) {
        apisWithConfig.push(api);
      } else {
        apisWithOutConfig.push(api);
      }
    });

    return apisWithConfig.concat(apisWithOutConfig);
  };


  return (
    <Card className="select-fields-section">
      <div className="select-api-fields-container">
        <span className="api-fields-title">{Constants.API_CONFIGURATION}</span>
        <div className="api-fields-description">
          {Constants.CONFIGURE_WEBHOOK_AND_FIELDS}
        </div>

        <div className="api-fields-container-card">
          <div className="api-fields-config">
            <div className="selected-api-list-container">
              <div className="api-section-div">
                <div className="api-section-title">API Name</div>
                <div className="api-section">
                  {!!cart?.length &&
                    sortApis(cart).map((element, index) => (
                      <div
                        key={index}
                        className={
                          selectedAPI?.apiId === element?.apiId
                            ? 'selected-api'
                            : 'unselected-api'
                        }
                      >
                        {checkIsAPIConfigured(element) ? (
                          <StyledIconCheck />
                        ) : null}
                        <ListItem
                          onClick={() => handleAPISelection(element)}
                          data-testid={`api-name-item-${index}`}
                          hideSelectedItemCheckmark={true}
                          className={
                            selectedAPI?.apiId === element?.apiId
                              ? 'selected-api-name'
                              : 'unselected-api-name'
                          }
                          id={`menu-list-item-${index}`}
                          tabIndex={index}
                          isDisabled={
                            !element?.details?.verticalScoping &&
                            element.apiType !== API_TYPES.ASYNC
                          }
                        >
                          {!element?.details?.verticalScoping &&
                          element.apiType !== API_TYPES.ASYNC ? (
                            <DivWithTooltip
                              text={element?.details?.name}
                              index={index}
                            />
                          ) : (
                            <div
                              className={
                                selectedAPI.apiId === element?.apiId
                                  ? 'selected-api-name'
                                  : 'unselected-api-name'
                              }
                            >
                              {element?.details?.name}
                            </div>
                          )}
                        </ListItem>
                      </div>
                    ))}
                </div>
              </div>
            </div>
            <div className="fields-container">
              {selectedAPI?.apiType === API_TYPES.ASYNC && (
                <div>
                  <div className="webhook-input-field-header">
                    <span className="fields-list-heading">API Webhook</span>
                  </div>
                  <div>
                    <div className="webhook-input">
                      <Input
                        id="webhook-input"
                        dataTestId="webhook-input"
                        label="Enter Webhook URL"
                        isRequired
                        name="input-simple"
                        onChange={(event) => {
                          handleWebhookInput(event.target.value);
                        }}
                        placeholder="Webhook URL"
                        value={selectedAPIWebhook || ''}
                        enableCustomValidation
                        hasError={!isValidUrl}
                        errorMessage="Website URL must be a valid URL. ex: https://fortellis.io"
                      />
                    </div>
                  </div>
                </div>
              )}
              <div
                className={
                  selectedAPI?.apiType !== API_TYPES.ASYNC
                    ? 'header-and-searchinput-wrapper'
                    : ''
                }
              >
                <div className="fields-listing-header">
                  <span className="fields-list-heading">API Fields</span>
                </div>
                {(selectedAPI?.details?.verticalScoping === false ||
                  !selectedAPI?.details?.verticalScoping) &&
                  selectedAPI?.apiType === API_TYPES.ASYNC && (
                    <div className="fields-listing-desc">
                      <span className="fields-list-desc">
                        Fields configuration is not needed for this API.
                      </span>
                    </div>
                  )}
                {selectedAPI?.details?.verticalScoping === true && (
                  <div className="search-input-wrapper">
                    <SearchInput
                      clearButtonLabel="clearButtonLabel"
                      id="search-input-expanded"
                      isAlwaysExpanded
                      name="search-input-expanded"
                      onChange={handleSearchInputChange}
                      placeholder="Search Fields"
                      size={INPUT_SIZES.LARGE}
                      value={searchInputValue}
                    />
                  </div>
                )}
              </div>
              {selectedAPI?.details?.verticalScoping === true && (
                <div className="api-fields-selection-table-container">
                  <table className="field-selection-table">
                    <thead>
                      <tr>
                        <th className="field-selection-column-header">
                          <div className="checkbox-wrapper">
                            <Checkbox
                              className="fields-checkbox"
                              dataTestId="checkbox"
                              id="Fields"
                              label="Field Name"
                              labelPosition="after"
                              name="Fields"
                              onChange={handleSelectAllCheckBox}
                              size="small"
                              checked={
                                selectedFieldMapping.get(selectedAPI.apiType === API_TYPES.REST ?`${API_TYPES.PROVIDER_ID_PREFIX}-${selectedAPI.apiId}`: selectedAPI.apiId)
                                  ?.length === selectedAPIFields?.length ||
                                false
                              }
                            />
                            {sortByAscending ? (
                              <IconArrowDropDown
                                role="sort-descending"
                                onClick={() => handleSortByAscending(false)}
                              />
                            ) : (
                              <IconArrowDropUp
                                role="sort-ascending"
                                onClick={() => handleSortByAscending(true)}
                              />
                            )}
                          </div>
                          <div className="selected-fields-count">
                            {selectedFieldMapping?.get(selectedAPI.apiType === API_TYPES.REST ?`${API_TYPES.PROVIDER_ID_PREFIX}-${selectedAPI.apiId}`: selectedAPI.apiId)
                              ?.length || 0}{' '}
                            of {selectedAPIFields?.length || 0} selected
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      {results &&
                        results?.map((element, index) => {
                          return (
                            <tr key={index} className="field-option-row">
                              <td className="field-option-element">
                                {' '}
                                <div className="field-version-wrapper">
                                  <Checkbox
                                    className="fields-checkbox"
                                    dataTestId="checkbox"
                                    role="api-field-checkbox"
                                    id={element.name}
                                    label={element.name}
                                    labelPosition="after"
                                    name={element.name}
                                    onChange={(event) =>
                                      handleSelectCheckBox(
                                        event,
                                        element,
                                        selectedAPI
                                      )
                                    }
                                    size="small"
                                    checked={
                                      selectedFieldMapping
                                        ?.get(selectedAPI.apiType === API_TYPES.REST ?`${API_TYPES.PROVIDER_ID_PREFIX}-${selectedAPI.apiId}`: selectedAPI.apiId)
                                        ?.includes(element.name) || false
                                    }
                                  />
                                  {selectedAPI.apiType !== API_TYPES.ASYNC && (
                                    <div className="versions-text">
                                      {element?.versionsText}
                                    </div>
                                  )}
                                </div>{' '}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        <OrderAPIStepperFooter
          setActiveStep={setActiveStep}
          activeStep={activeStep}
          isContinueDisabled={isContinueDisabled}
          step={ORDER_APIS_STEPPER_FOOTER.CONFIGURE_FIELDS}
        />
      </div>
    </Card>
  );
};

export default SelectAPIFields;
